import { BusinessType } from '@/constant';
import { insertObjectIfElse } from '@/utils';
import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import { includes, omit } from 'ramda';

import {
	formatWorkerProgramAllList,
	formatWorkerProgramApplicantList,
	formatWorkerProgramDetail,
	formatWorkerProgramExcelList,
	formatWorkerProgramFranchiseList,
	formatWorkerProgramList,
} from './worker-program.helper';
import { WorkerProgramActions } from './worker-program.redux';
import {
	IPayloadCreateWorkerProgramApplicantRequest,
	IPayloadCreateWorkerProgramFranchiseRequest,
	IPayloadCreateWorkerProgramRequest,
	IPayloadDeleteWorkerProgramApplicantByIdRequest,
	IPayloadDeleteWorkerProgramFranchiseByIdRequest,
	IPayloadDeleteWorkerProgramRequest,
	IPayloadGetWorkerProgramAllListRequest,
	IPayloadGetWorkerProgramApplicantListRequest,
	IPayloadGetWorkerProgramApplicantStatusRequest,
	IPayloadGetWorkerProgramDetailRequest,
	IPayloadGetWorkerProgramExcelListRequest,
	IPayloadGetWorkerProgramFranchiseListRequest,
	IPayloadGetWorkerProgramFranchiseStatusRequest,
	IPayloadGetWorkerProgramListRequest,
} from './worker-program.type';

export function* getWorkerProgramListSaga(
	action: PayloadAction<IPayloadGetWorkerProgramListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerProgramListResponse> =
			yield API.getWorkerProgramList({ searchTypes, ...rest });
		const content = formatWorkerProgramList(res?.data?.content);

		yield put(
			WorkerProgramActions.getWorkerProgramListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramListFailure());
	}
}

export function* getWorkerProgramExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerProgramExcelListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerProgramListResponse> =
			yield API.getWorkerProgramList({ searchTypes, ...rest });
		const content = formatWorkerProgramExcelList(res?.data?.content);

		yield put(WorkerProgramActions.getWorkerProgramExcelListSuccess());
		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerProgramActions.getWorkerProgramExcelListFailure());
	}
}

export function* deleteWorkerProgram(
	action: PayloadAction<IPayloadDeleteWorkerProgramRequest>
) {
	const { callback, ids } = action.payload;

	try {
		yield API.deleteWorkerProgram(ids);

		yield put(WorkerProgramActions.deleteWorkerProgramSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.deleteWorkerProgramFailure());
	}
}

export function* createWorkerPrograms(
	action: PayloadAction<IPayloadCreateWorkerProgramRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		yield API.createWorkerProgram({
			...props,
		});

		yield put(WorkerProgramActions.createWorkerProgramSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.createWorkerProgramFailure());
	}
}

export function* getWorkerProgramDetail(
	action: PayloadAction<IPayloadGetWorkerProgramDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerProgramDetailResponse> =
			yield API.getWorkerProgramDetail(id);

		const workerProgramDetail = formatWorkerProgramDetail(res.data);

		yield put(
			WorkerProgramActions.getWorkerProgramDetailSuccess({
				workerProgramDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramDetailFailure());
	}
}

// TODO: program applicant
export function* getWorkerProgramApplicantList(
	action: PayloadAction<IPayloadGetWorkerProgramApplicantListRequest>
) {
	const { callback, id, params } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerProgramApplicantListResponse> =
			yield API.getWorkerProgramApplicantList(id, params);
		const content = formatWorkerProgramApplicantList(res?.data?.content);

		yield put(
			WorkerProgramActions.getWorkerProgramApplicantListSuccess({
				...params,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramApplicantListFailure());
	}
}
export function* deleteWorkerProgramApplicantList(
	action: PayloadAction<IPayloadDeleteWorkerProgramApplicantByIdRequest>
) {
	const { callback, programId, ids } = action.payload;

	try {
		// call api
		yield API.deleteWorkerProgramApplicantList(programId, ids);

		yield put(WorkerProgramActions.deleteWorkerProgramApplicantByIdSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.deleteWorkerProgramApplicantByIdFailure());
	}
}
export function* createWorkerProgramApplicant(
	action: PayloadAction<IPayloadCreateWorkerProgramApplicantRequest>
) {
	const { callback, ...data } = action.payload;

	try {
		yield API.createWorkerProgramApplicant(data);

		yield put(WorkerProgramActions.createWorkerProgramApplicantSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.createWorkerProgramApplicantFailure());
	}
}

// TODO: program franchise
export function* getWorkerProgramFranchiseList(
	action: PayloadAction<IPayloadGetWorkerProgramFranchiseListRequest>
) {
	const { callback, id, params } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerProgramFranchiseListResponse> =
			yield API.getWorkerProgramFranchiseList(id, {
				...omit(['typeDetail'], params),
				...insertObjectIfElse(
					includes(params?.typeDetail, Object.values(BusinessType)),
					{
						type: params?.typeDetail,
					},
					{
						typeDetail: params?.typeDetail,
					}
				),
			});

		const content = formatWorkerProgramFranchiseList(res?.data?.content);

		yield put(
			WorkerProgramActions.getWorkerProgramFranchiseListSuccess({
				...params,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramFranchiseListFailure());
	}
}
export function* deleteWorkerProgramFranchiseList(
	action: PayloadAction<IPayloadDeleteWorkerProgramFranchiseByIdRequest>
) {
	const { callback, programId, ids } = action.payload;

	try {
		// call api
		yield API.deleteWorkerProgramFranchiseList(programId, ids);

		yield put(WorkerProgramActions.deleteWorkerProgramFranchiseByIdSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.deleteWorkerProgramFranchiseByIdFailure());
	}
}
export function* createWorkerProgramFranchises(
	action: PayloadAction<IPayloadCreateWorkerProgramFranchiseRequest>
) {
	const { callback, ...data } = action.payload;

	try {
		yield API.createWorkerProgramFranchises(data);

		yield put(WorkerProgramActions.createWorkerProgramFranchiseSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.createWorkerProgramFranchiseFailure());
	}
}

export function* getWorkerProgramAllList(
	action: PayloadAction<IPayloadGetWorkerProgramAllListRequest>
) {
	const { callback } = action.payload || {};

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerProgramListAllResponse> =
			yield API.getWorkerProgramAllList();

		const content = formatWorkerProgramAllList(res?.data);

		yield put(
			WorkerProgramActions.getWorkerProgramAllListSuccess({
				content,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramAllListFailure());
	}
}

export function* getWorkerProgramApplicantStatus(
	action: PayloadAction<IPayloadGetWorkerProgramApplicantStatusRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerProgramApplicantStatusResponse> =
			yield API.getWorkerProgramApplicantStatus(id);

		yield put(
			WorkerProgramActions.getWorkerProgramApplicantStatusSuccess({
				content: res?.data,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramApplicantStatusFailure());
	}
}

export function* getWorkerProgramFranchiseStatus(
	action: PayloadAction<IPayloadGetWorkerProgramFranchiseStatusRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerProgramFranchiseStatusResponse> =
			yield API.getWorkerProgramFranchiseStatus(id);

		yield put(
			WorkerProgramActions.getWorkerProgramFranchiseStatusSuccess({
				content: res?.data,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerProgramActions.getWorkerProgramFranchiseStatusFailure());
	}
}
